import React, { useState, useEffect } from 'react';
import { X, MapPin, Save, Loader2 } from 'lucide-react';
import Swal from 'sweetalert2';
import { cityApi } from '../services/api';

const RouteModal = ({ isOpen, onClose, onSave, route }) => {
  const [formData, setFormData] = useState({
    fromCityID: '',
    toCityID: '',
    distance: 0,
    estimatedDuration: 0,
    hours: 0,
    minutes: 0,
    basePrice: 0,
    serviceCharge: 150,
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);

  const isEditing = !!route;

  // Load cities when modal opens
  useEffect(() => {
    if (isOpen) {
      loadCities();
    }
  }, [isOpen]);

  // Load cities from API
  const loadCities = async () => {
    try {
      setCitiesLoading(true);
      const response = await cityApi.getCities(true); // Get only active cities
      
      // Handle different response structures
      let cities = [];
      if (response?.value && Array.isArray(response.value)) {
        cities = response.value;
      } else if (Array.isArray(response)) {
        cities = response;
      } else if (response && typeof response === 'object') {
        cities = [response];
      }
      
      setCities(cities);
      
      // If no cities loaded, show a helpful message
      if (cities.length === 0) {
        setApiError('No cities available. Please add cities first before creating routes.');
      }
    } catch (error) {
      console.error('Error loading cities:', error);
      setApiError('Failed to load cities. Please ensure cities are added to the system first.');
      setCities([]);
    } finally {
      setCitiesLoading(false);
    }
  };

  useEffect(() => {
    if (route) {
      setFormData({
        fromCityID: route.fromCityID || '',
        toCityID: route.toCityID || '',
        distance: route.distance || 0,
        estimatedDuration: route.estimatedDuration || 0,
        hours: Math.floor(route.estimatedDuration / 60),
        minutes: route.estimatedDuration % 60,
        basePrice: route.basePrice || 0,
        serviceCharge: route.serviceCharge || 150,
        description: route.description || ''
      });
    } else {
      setFormData({
        fromCityID: '',
        toCityID: '',
        distance: 0,
        estimatedDuration: 0,
        hours: 0,
        minutes: 0,
        basePrice: 0,
        serviceCharge: 150,
        description: ''
      });
    }
    setErrors({});
  }, [route]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fromCityID) {
      newErrors.fromCityID = 'From location is required';
    }

    if (!formData.toCityID) {
      newErrors.toCityID = 'To location is required';
    }

    if (formData.fromCityID === formData.toCityID) {
      newErrors.toCityID = 'From and To locations cannot be the same';
    }

    if (formData.distance <= 0) {
      newErrors.distance = 'Distance must be greater than 0';
    }

    if (formData.estimatedDuration <= 0) {
      newErrors.estimatedDuration = 'Estimated duration must be greater than 0';
    }

    if (formData.basePrice <= 0) {
      newErrors.basePrice = 'Base price must be greater than 0';
    }

    if (formData.serviceCharge < 0) {
      newErrors.serviceCharge = 'Service charge cannot be negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setApiError('');
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error saving route:', error);
      setApiError(error.message || 'Failed to save route. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />
        
        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">
                    {isEditing ? 'Edit Route' : 'Add New Route'}
                  </h3>
                  <p className="text-blue-100">
                    {isEditing ? 'Update route information' : 'Enter route details'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                disabled={loading}
                className="text-white hover:text-gray-200 transition-colors disabled:opacity-50"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 py-6">
            {/* API Error Message */}
            {apiError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
                <strong>Error:</strong> {apiError}
                <button 
                  onClick={() => setApiError('')}
                  className="float-right font-bold text-red-700 hover:text-red-900"
                >
                  ×
                </button>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* From & To Locations */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    From Location *
                  </label>
                  {citiesLoading ? (
                    <div className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 flex items-center justify-center">
                      <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                      <span className="ml-2 text-gray-500">Loading cities...</span>
                    </div>
                  ) : (
                    <select
                      name="fromCityID"
                      value={formData.fromCityID}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.fromCityID ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select departure city</option>
                      {cities.map(city => (
                        <option key={city.cityID} value={city.cityID}>
                          {city.cityName}
                        </option>
                      ))}
                    </select>
                  )}
                  {errors.fromCityID && (
                    <p className="mt-1 text-sm text-red-600">{errors.fromCityID}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    To Location *
                  </label>
                  {citiesLoading ? (
                    <div className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 flex items-center justify-center">
                      <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                      <span className="ml-2 text-gray-500">Loading cities...</span>
                    </div>
                  ) : (
                    <select
                      name="toCityID"
                      value={formData.toCityID}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.toCityID ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select destination city</option>
                      {cities.map(city => (
                        <option key={city.cityID} value={city.cityID}>
                          {city.cityName}
                        </option>
                      ))}
                    </select>
                  )}
                  {errors.toCityID && (
                    <p className="mt-1 text-sm text-red-600">{errors.toCityID}</p>
                  )}
                </div>
              </div>

              {/* Distance & Duration */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Distance (km) *
                  </label>
                  <input
                    type="number"
                    name="distance"
                    value={formData.distance}
                    onChange={handleInputChange}
                    min="0"
                    step="0.1"
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.distance ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., 230.5"
                  />
                  {errors.distance && (
                    <p className="mt-1 text-sm text-red-600">{errors.distance}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estimated Duration (hours & minutes) *
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Hours</label>
                      <input
                        type="number"
                        name="hours"
                        value={formData.hours}
                        min="0"
                        max="24"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0"
                        onChange={(e) => {
                          const hours = parseInt(e.target.value) || 0;
                          const minutes = parseInt(formData.minutes) || 0;
                          setFormData(prev => ({
                            ...prev,
                            hours: hours,
                            estimatedDuration: (hours * 60) + minutes
                          }));
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Minutes</label>
                      <input
                        type="number"
                        name="minutes"
                        value={formData.minutes}
                        min="0"
                        max="59"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0"
                        onChange={(e) => {
                          const hours = parseInt(formData.hours) || 0;
                          const minutes = parseInt(e.target.value) || 0;
                          setFormData(prev => ({
                            ...prev,
                            minutes: minutes,
                            estimatedDuration: (hours * 60) + minutes
                          }));
                        }}
                      />
                    </div>
                  </div>
                  <div className="mt-2 text-sm text-gray-500">
                    Total: {Math.floor(formData.estimatedDuration / 60)} hr {formData.estimatedDuration % 60} min
                  </div>
                  {errors.estimatedDuration && (
                    <p className="mt-1 text-sm text-red-600">{errors.estimatedDuration}</p>
                  )}
                </div>
              </div>

              {/* Base Price & Service Charge */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Base Price (Rs) *
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">Rs</span>
                    <input
                      type="number"
                      name="basePrice"
                      value={formData.basePrice}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className={`w-full pl-12 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.basePrice ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="0.00"
                    />
                  </div>
                  {errors.basePrice && (
                    <p className="mt-1 text-sm text-red-600">{errors.basePrice}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service Charge (Rs) *
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">Rs</span>
                    <input
                      type="number"
                      name="serviceCharge"
                      value={formData.serviceCharge}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className={`w-full pl-12 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.serviceCharge ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="150.00"
                    />
                  </div>
                  {errors.serviceCharge && (
                    <p className="mt-1 text-sm text-red-600">{errors.serviceCharge}</p>
                  )}
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Optional route description..."
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  className="px-6 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Save className="w-5 h-5" />
                  )}
                  <span>{isEditing ? 'Update Route' : 'Create Route'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RouteModal;
